/* This example requires Tailwind CSS v2.0+ */
import { CheckIcon } from "@heroicons/react/solid";
import PropTypes from "prop-types";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function CircleSteps({ steps, orientation = "vertical" }) {
  const isVertical = orientation === "vertical";

  return (
    <nav aria-label="Progress">
      <ol
        className={classNames(
          "overflow-hidden",
          isVertical ? "" : "flex items-center"
        )}
      >
        {steps.map((step, stepIdx) => (
          <li
            key={step.name}
            className={classNames(
              stepIdx !== steps.length - 1
                ? isVertical
                  ? "pb-10"
                  : "pr-8 sm:pr-20"
                : "",
              "relative",
              isVertical ? "" : "flex-1"
            )}
          >
            {step.status === "complete" ? (
              <>
                {stepIdx !== steps.length - 1 ? (
                  <div
                    className={classNames(
                      "absolute bg-black",
                      isVertical
                        ? "top-4 left-4 -ml-px mt-0.5 h-full w-0.5"
                        : "top-4 left-1/2 right-0 h-0.5 -mr-8 sm:-mr-20"
                    )}
                    aria-hidden="true"
                  />
                ) : null}
                <a
                  href={step.href}
                  className={classNames(
                    "group relative flex",
                    isVertical ? "items-start" : "flex-col items-center"
                  )}
                >
                  <span className="flex h-9 items-center">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black group-hover:bg-gray-800">
                      <CheckIcon
                        className="h-5 w-5 text-white"
                        aria-hidden="true"
                      />
                    </span>
                  </span>
                  <span
                    className={classNames(
                      "flex min-w-0 flex-col",
                      isVertical ? "ml-4" : "mt-2 text-center"
                    )}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      {step.name}
                    </span>
                    <span className="text-sm text-gray-500">
                      {step.description}
                    </span>
                  </span>
                </a>
              </>
            ) : step.status === "current" ? (
              <>
                {stepIdx !== steps.length - 1 ? (
                  <div
                    className={classNames(
                      "absolute bg-gray-300",
                      isVertical
                        ? "top-4 left-4 -ml-px mt-0.5 h-full w-0.5"
                        : "top-4 left-1/2 right-0 h-0.5 -mr-8 sm:-mr-20"
                    )}
                    aria-hidden="true"
                  />
                ) : null}
                <a
                  href={step.href}
                  className={classNames(
                    "group relative flex",
                    isVertical ? "items-start" : "flex-col items-center"
                  )}
                  aria-current="step"
                >
                  <span className="flex h-9 items-center" aria-hidden="true">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-black bg-white">
                      <span className="h-2.5 w-2.5 rounded-full bg-black" />
                    </span>
                  </span>
                  <span
                    className={classNames(
                      "flex min-w-0 flex-col",
                      isVertical ? "ml-4" : "mt-2 text-center"
                    )}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide text-black">
                      {step.name}
                    </span>
                    <span className="text-sm text-gray-500">
                      {step.description}
                    </span>
                  </span>
                </a>
              </>
            ) : (
              <>
                {stepIdx !== steps.length - 1 ? (
                  <div
                    className={classNames(
                      "absolute bg-gray-300",
                      isVertical
                        ? "top-4 left-4 -ml-px mt-0.5 h-full w-0.5"
                        : "top-4 left-1/2 right-0 h-0.5 -mr-8 sm:-mr-20"
                    )}
                    aria-hidden="true"
                  />
                ) : null}
                <a
                  href={step.href}
                  className={classNames(
                    "group relative flex",
                    isVertical ? "items-start" : "flex-col items-center"
                  )}
                >
                  <span className="flex h-9 items-center" aria-hidden="true">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white group-hover:border-gray-400">
                      <span className="h-2.5 w-2.5 rounded-full bg-transparent group-hover:bg-gray-300" />
                    </span>
                  </span>
                  <span
                    className={classNames(
                      "flex min-w-0 flex-col",
                      isVertical ? "ml-4" : "mt-2 text-center"
                    )}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {step.name}
                    </span>
                    <span className="text-sm text-gray-400">
                      {step.description}
                    </span>
                  </span>
                </a>
              </>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

CircleSteps.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      status: PropTypes.oneOf(["upcoming", "current", "complete"]),
    }).isRequired
  ).isRequired,
  orientation: PropTypes.oneOf(["vertical", "horizontal"]),
};
